import { useEffect, useMemo, useState, useCallback } from "react";
import { type HttpError, useCustomMutation, useModal, useTable, useTranslate, useInvalidate } from "@refinedev/core";
import { IOrder } from "@/utils/zod/orderSchema";
import { MaterialReactTable, MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { Typography, IconButton, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import { Skeleton } from "@mui/material";
import { CustomTooltip, RefineListView } from "@/components";
import { IProduct } from "@/utils/zod/productCollectionFormValidation";
import ProductShowModal from "@/components/product/ProductShowModal";
import { FormProvider, useForm } from "react-hook-form";
import RHFAutoCompleteString from "@/components/form/RHFAutocompleteString";
import { DateField, DeleteButton } from "@refinedev/mui";
import { formatFileType } from "@/utils/formattingFunctions";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";

export const OrderList = () => {
    const t = useTranslate();
    const invalidate = useInvalidate();

    const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
    const [downloadingRows, setDownloadingRows] = useState<Record<string, boolean>>({});
    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const { show: showDeleteConfirmationModal, close: closeDeleteConfirmationModal, visible: deleteConfirmationModalVisible } = useModal();

    const preloadImages = async (products: any[]) => {
        const promises = products.map(product => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = product?.medias?.[0]?.cdn_url.replace('image', 'image/compressed') || '';
            });
        });
        return Promise.all(promises);
    };

    const handleProductShow = useCallback((product: IProduct) => {
        setSelectedProduct(product);
    }, []);

    const handleModalClose = () => {
        setSelectedProduct(null);
    };

    const { tableQuery } = useTable<IOrder[], HttpError, IOrder>({
        resource: "orders",
    });

    const { mutateAsync: bulkDeleteMutate } = useCustomMutation();

    const methods = useForm<IOrder>();

    const orders = tableQuery?.data?.data ?? [];
    const { mutateAsync: updateMutate } = useCustomMutation();

    const columns = useMemo<MRT_ColumnDef<IOrder>[]>(
        () => [
            //id
            {
                accessorKey: 'id',
                header: '#id',
                Cell: ({ row }) => <Typography>{row.id}</Typography>,
                size: 60,
            },

            //ship_to_ledger_name
            {
                accessorKey: 'customer',
                header: 'Customer',
                size: 100,
                enableSorting: false,
                accessorFn: (row) => row.ship_to_ledger_name || '-'
            },

            //ship_to.agent_ledger_name
            {
                accessorKey: 'agent',
                header: 'Agent',
                size: 140,
                enableSorting: false,
                accessorFn: (row) => row.ship_to?.agent_ledger_name || '-'
            },

            {
                accessorKey: "order_products",
                header: t("collections.fields.product"),
                size: 220,
                Cell: ({ row }) => {
                    useEffect(() => {
                        if (!loadingRows[row.id]) {
                            setLoadingRows(prev => ({ ...prev, [row.id]: true }));
                            preloadImages(row.original.order_products).then(() => {
                                setLoadingRows(prev => ({ ...prev, [row.id]: false }));
                            });
                        }
                    }, [row.id]);

                    return (
                        <Box display="flex" alignItems="flex-start" gap="8px" flexWrap="wrap">
                            {loadingRows[row.id] ? (
                                Array.from({ length: row.original.order_products.length }).map((_, index) => (
                                    <Skeleton
                                        key={index}
                                        sx={{ width: "32px", height: "32px" }}
                                        variant="rectangular"
                                    />
                                ))
                            ) : (
                                row.original.order_products.map((order_product: any) => {
                                    const product: IProduct = order_product.product;
                                    return (
                                        <CustomTooltip key={product?.id} title={product?.name}>
                                            <Box
                                                component={formatFileType(product.medias[0].type)}
                                                sx={{ width: "32px", height: "32px", cursor: "pointer" }}
                                                alt={product?.name}
                                                src={product?.medias?.[0]?.cdn_url}
                                                onClick={() => handleProductShow(product)}
                                            />
                                        </CustomTooltip>
                                    )
                                })
                            )}
                        </Box>
                    );
                },
            },

            //order_status
            {
                accessorKey: 'order_status',
                header: 'Order Status',
                size: 180,
                enableSorting: false,
                Cell: ({ row }) => {

                    return (
                        <RHFAutoCompleteString
                            name={`status_${row.original.id}`}
                            multiple={false}
                            createNew={false}
                            defaultValue={row.original.status}
                            label=""
                            options={["pending", "confirmed", "shipped", "delivered", "cancelled"]}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    height: 40,
                                }
                            }}
                            onOptionChange={(option) => {
                                updateMutate({
                                    url: `/orders/${row.original.id}`,
                                    method: "patch",
                                    values: { status: option },
                                    successNotification: {
                                        message: "Order status updated successfully",
                                        type: "success",
                                    },
                                    errorNotification: {
                                        message: "Error updating order status",
                                        type: "error",
                                    },
                                });
                            }}
                        />
                    );
                }
            },

            //total_amount of all the products combined
            {
                accessorKey: 'total_amount',
                header: 'Total Amount',
                size: 100,
                align: 'right',
                accessorFn: (row) => {
                    return (row.order_products || []).reduce((acc, curr) => acc + (curr.total_amount || 0), 0);
                },
                Cell: ({ cell }) => {
                    const total = cell.getValue<number>();
                    return total ? new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR'
                    }).format(total) : '-';
                }
            },

            //total_qty of all the products combined
            {
                accessorKey: 'total_qty',
                header: 'Total Qty',
                size: 100,
                accessorFn: (row) => row.order_products.reduce((acc, curr) => acc + (curr.quantity || 0), 0) || '-'
            },

            //ship_to_address.branch_name
            {
                accessorKey: 'ship_to',
                header: 'Ship To',
                size: 140,
                enableSorting: false,
                accessorFn: (row) => row.ship_to_address?.branch_name || '-'
            },

            //order_date
            {
                accessorKey: 'order_date',
                header: 'Order Date',
                size: 140,
                Cell: ({ cell }) => {
                    const date = cell.getValue<string>();
                    return date ? <DateField value={date} format="DD MMM YYYY" /> : '-';
                }
            },

            //remarks
            {
                accessorKey: 'remarks',
                header: 'Remarks',
                size: 140,
                Cell: ({ cell }) => {
                    const remarks = cell.getValue<string>();
                    return remarks ? remarks : '-';
                }
            },

            //actions column
            {
                id: 'actions',
                accessorKey: 'actions',
                header: 'Actions',
                size: 100,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const handleDownload = async () => {
                        try {
                            // Set downloading state for this row
                            setDownloadingRows(prev => ({ ...prev, [row.original.id]: true }));
                            
                            const pdf = await fetch('https://serverless-pdf-kayaan-two.vercel.app/api/order-form', {
                                method: 'POST',
                                body: JSON.stringify({
                                    ...row.original,
                                    order_products: row.original.order_products.map((order_product) => ({
                                        ...order_product,
                                        product: {
                                            ...order_product.product,
                                            medias: order_product.product.medias.map((media) => ({
                                                ...media,
                                                cdn_url: media.cdn_url.replace('image', 'image/compressed'),
                                            }))
                                        }
                                    })),
                                }),
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                            })
                            const pdfBlob = await pdf.blob();
                            const pdfUrl = URL.createObjectURL(pdfBlob);
                            
                            // Create a temporary anchor element to trigger download
                            const link = document.createElement('a');
                            link.href = pdfUrl;
                            link.download = `order-${row.original.id}.pdf`; // Set filename
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            // Clean up the object URL
                            URL.revokeObjectURL(pdfUrl);
                        } catch (error) {
                            console.error('Error downloading order:', error);
                        } finally {
                            // Remove downloading state for this row
                            setDownloadingRows(prev => ({ ...prev, [row.original.id]: false }));
                        }
                    };

                    const isDownloading = downloadingRows[row.original.id];

                    return (
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                onClick={handleDownload}
                                size="small"
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <DownloadIcon fontSize="small" />
                                )}
                            </IconButton>
                            <DeleteButton hideText recordItemId={row.id} />
                        </Stack>)
                },
            },
        ],
        [t, loadingRows, handleProductShow, downloadingRows]
    );

    const selectedRowIds = Object.keys(rowSelection).filter(key => rowSelection[key]);
    const hasSelectedRows = selectedRowIds.length > 0;

    const handleDeleteClick = () => {
        showDeleteConfirmationModal();
    };

    const handleDeleteConfirm = async () => {
        try {
            await bulkDeleteMutate({
                url: "/orders/bulk",
                method: "delete",
                values: selectedRowIds,
                successNotification: {
                    message: "Orders deleted successfully",
                    description: `${selectedRowIds.length} order${selectedRowIds.length > 1 ? 's' : ''} deleted successfully`,
                    type: "success",
                },
                errorNotification: {
                    message: "Error deleting orders",
                    description: "Failed to delete selected orders",
                    type: "error",
                },
            });
            
            // Invalidate and refetch the orders data
            await invalidate({
                resource: "orders",
                invalidates: ["list"],
            });
            
        } catch (error) {
            console.error('Error deleting orders:', error);
        } finally {
            closeDeleteConfirmationModal();
            setRowSelection({});
        }
    };

    const handleDeleteCancel = () => {
        closeDeleteConfirmationModal();
        setRowSelection({});
    };

    return (
        <RefineListView headerButtons={[]}>
            <FormProvider {...methods}>
                <MaterialReactTable
                    columns={columns}
                    data={orders}
                    enableTopToolbar
                    enableBottomToolbar
                    enableColumnFilters
                    enableGlobalFilter
                    enablePagination
                    enableRowSelection
                    onRowSelectionChange={setRowSelection}
                    muiTableBodyRowProps={{ hover: true }}
                    muiPaginationProps={{
                        rowsPerPageOptions: [10, 20, 40, 80],
                        showFirstButton: true,
                        showLastButton: true
                    }}
                    state={{
                        isLoading: tableQuery?.isLoading,
                        showGlobalFilter: true,
                        rowSelection,
                    }}
                    muiToolbarAlertBannerProps={
                        tableQuery?.isError
                            ? {
                                color: 'error',
                                children: 'Error loading data',
                            }
                            : undefined
                    }
                    enableColumnResizing
                    enableColumnPinning
                    enableFullScreenToggle
                    enableDensityToggle
                    enableGlobalFilterModes
                    positionGlobalFilter="right"
                    getRowId={(row) => row.id?.toString() ?? ''}
                    initialState={{
                        density: 'compact',
                        pagination: { pageSize: 10, pageIndex: 0 },
                        columnPinning: { left: ['mrt-row-select', 'id'], right: ['order_status', 'actions'] },
                        sorting: [{ id: 'id', desc: true }],
                    }}
                    renderBottomToolbarCustomActions={() => (
                        hasSelectedRows ? (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteClick}
                                sx={{ ml: 2 }}
                            >
                                Delete Orders ({selectedRowIds.length})
                            </Button>
                        ) : <div />
                    )}
                />
            </FormProvider>

            {selectedProduct && (
                <ProductShowModal
                    product={selectedProduct}
                    open={true}
                    onClose={handleModalClose}
                />
            )}

            <Dialog
                open={deleteConfirmationModalVisible}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete {selectedRowIds.length} selected order{selectedRowIds.length > 1 ? 's' : ''}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="inherit">
                        No
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </RefineListView>
    );
};
